/* --- START EDIT: Monochrome-friendly disabled states and refined variants (visual only) --- */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"

const buttonVariants = cva(
  // Remove hard opacity rule; keep pointer-events none; let variants define disabled visuals.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4",
  {
    variants: {
      variant: {
        // Filled light pill on dark; hover darkens slightly; disabled becomes subtle neutral
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 active:bg-primary/80 disabled:bg-white/6 disabled:text-muted-foreground disabled:shadow-none",

        destructive:
          "bg-destructive text-white shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 disabled:opacity-60",

        // Outline matches header controls; hover gets a faint fill
        outline:
          "border bg-transparent shadow-sm hover:bg-foreground/[0.05] text-foreground disabled:text-muted-foreground disabled:border-border disabled:opacity-70",

        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/85 disabled:opacity-70",

        ghost:
          "hover:bg-foreground/[0.05] text-foreground disabled:opacity-60",

        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
/* --- END EDIT: Monochrome-friendly disabled states and refined variants (visual only) --- */